export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** Called once when any authenticated request comes back 401 — i.e. the
 *  session cookie expired mid-visit. The AuthProvider registers a handler
 *  that drops the cached user, which makes the Layout guard bounce the
 *  browser to /login instead of leaving every page to render its own
 *  "failed to load" banner against a dead session. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

/** The auth endpoints answer 401 as a normal outcome — a wrong password on
 *  login, "not signed in" on the boot-time /me probe. Those must not be
 *  mistaken for an expired session. */
const AUTH_PATHS = ["/api/auth/login", "/api/auth/me", "/api/auth/register"];

/** Thin fetch wrapper: JSON in/out, cookies on by default, raises ApiError on non-2xx. */
export async function api<T = unknown>(
  path: string,
  opts: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = opts;
  const init: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers || {}),
    },
  };
  if (json !== undefined) init.body = JSON.stringify(json);

  const resp = await fetch(path, init);

  if (resp.status === 204) return undefined as T;

  let body: unknown = null;
  const ct = resp.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    body = await resp.json().catch(() => null);
  } else {
    body = await resp.text().catch(() => null);
  }

  if (!resp.ok) {
    if (
      resp.status === 401 &&
      onUnauthorized &&
      !AUTH_PATHS.some((p) => path.split("?")[0] === p)
    ) {
      onUnauthorized();
    }
    throw new ApiError(resp.status, body, extractErrorMessage(body));
  }

  return body as T;
}

/** Turn an error response body into a human-readable message.
 *
 *  FastAPI hands back two shapes: a plain ``{detail: "some_code"}`` for
 *  hand-raised HTTPExceptions, and ``{detail: [{loc, msg, type}, ...]}``
 *  for request-validation (422) failures. The array form used to fall
 *  through to a generic "Request failed", which left users staring at a
 *  useless error with no idea which field was wrong — flatten it into
 *  ``field: message`` lines instead. */
function extractErrorMessage(body: unknown): string {
  if (typeof body === "string" && body) return body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string" && detail) return detail;
    // Structured-error shape: routes hand back ``{error, reason, hint,
    // ...}`` when they want to give the UI a one-line user-facing
    // message AND a stable error code. The ``hint`` field — when
    // present — is the broker-readable explanation; ``error`` is the
    // machine code we fall back to for non-localized callers.
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const d = detail as Record<string, unknown>;
      if (typeof d.hint === "string" && d.hint) return d.hint;
      if (typeof d.error === "string" && d.error) return d.error;
    }
    if (Array.isArray(detail)) {
      const parts = detail
        .map((d) => {
          if (d && typeof d === "object" && "msg" in d) {
            const item = d as { msg: unknown; loc?: unknown };
            const field = Array.isArray(item.loc)
              ? item.loc.filter((p) => p !== "body" && p !== "query").join(".")
              : "";
            return field ? `${field}: ${item.msg}` : String(item.msg);
          }
          return typeof d === "string" ? d : null;
        })
        .filter((p): p is string => Boolean(p));
      if (parts.length) return parts.join("; ");
    }
  }
  return "Request failed";
}
